import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { fmtCurrency } from "../lib/status";
import { severityStyle } from "../lib/warnings";
import { AlertTriangle, CalendarClock, Calculator, Pencil, ChevronDown, ChevronUp, Wrench, AlertOctagon, MapPin, MessageSquare, CalendarRange } from "lucide-react";
import BookingLocationPicker from "../components/BookingLocationPicker";

function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function isoDateToLocalInput(isoDate, hour = 10) {
  // "2026-06-20" -> "2026-06-20T10:00" (default to 10:00 local)
  if (!isoDate || typeof isoDate !== "string") return null;
  const match = isoDate.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!match) return null;
  const h = String(hour).padStart(2, "0");
  return `${match[1]}T${h}:00`;
}

function findVehicleMatch(vehicles, preferredText) {
  if (!preferredText || !vehicles?.length) return null;
  const q = preferredText.toLowerCase();
  // Try exact-ish match on name, plate, or "type"
  return vehicles.find((v) =>
    [v.name, v.plate_number, v.vehicle_type, v.make, v.model]
      .filter(Boolean)
      .some((s) => String(s).toLowerCase().includes(q) || q.includes(String(s).toLowerCase()))
  ) || null;
}

function computeDays(startStr, endStr) {
  if (!startStr || !endStr) return 0;
  const s = new Date(startStr);
  const e = new Date(endStr);
  if (isNaN(s) || isNaN(e) || e <= s) return 0;
  const hours = (e - s) / (1000 * 3600);
  return Math.max(1, Math.ceil(hours / 24));
}

export default function BookingNew() {
  const { company } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const fromInquiryId = searchParams.get("from_inquiry") || null;
  // Phase 2F: Fleet Calendar's click-empty-slot handoff: ?vehicle_id=&start=&end=
  const prefillVehicleId = searchParams.get("vehicle_id") || null;
  const prefillStart = searchParams.get("start") || null;
  const prefillEnd = searchParams.get("end") || null;
  const origin = prefillVehicleId ? "fleet_calendar" : null;
  const [fromInquiry, setFromInquiry] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const now = new Date();
  const start = new Date(now.getTime() + 24 * 3600 * 1000);
  const end = new Date(now.getTime() + 72 * 3600 * 1000);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    item_id: "",
    start_datetime: toLocalInput(start),
    end_datetime: toLocalInput(end),
    pickup_location: "",
    return_location: "",
    pickup_location_data: null,
    return_location_data: null,
    delivery_required: false,
    return_same_as_pickup: false,
    rental_service_type: "self_drive",
    price: 0,
    deposit_required: 0,
    // Part of the rental price paid up front. Deliberately separate from the
    // refundable security deposit above.
    advance_payment_required: 0,
    internal_notes: "",
    customer_visible_notes: "Please bring valid ID and driver's license.",
    send_confirmation_now: true,
  });
  const [businessLocation, setBusinessLocation] = useState(null);
  const [showPickupMap, setShowPickupMap] = useState(false);
  const [showReturnMap, setShowReturnMap] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [inlineWarnings, setInlineWarnings] = useState(null);
  const [overrideNote, setOverrideNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [priceManual, setPriceManual] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      const list = r.data.filter((v) => v.status !== "retired");
      setVehicles(list);
      if (list[0]) setForm((f) => (f.item_id ? f : { ...f, item_id: list[0].id }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill from inquiry when ?from_inquiry=<id> is present.
  useEffect(() => {
    if (!fromInquiryId) return;
    api.get(`/inquiries/${fromInquiryId}`).then(({ data }) => {
      setFromInquiry(data);
      if (data.status === "converted" && data.converted_booking_id) {
        toast.info("This inquiry has already been converted. Opening the existing booking.");
        nav(`/app/bookings/${data.converted_booking_id}`, { replace: true });
        return;
      }
      // Pre-fill known fields. We keep the user's ability to edit everything.
      const startInput = isoDateToLocalInput(data.start_date, 10);
      const endInput = isoDateToLocalInput(data.end_date, 18);
      const notesParts = [];
      if (data.preferred_vehicle) notesParts.push(`Preferred vehicle: ${data.preferred_vehicle}`);
      if (data.pickup_location) notesParts.push(`Pickup wish: ${data.pickup_location}`);
      if (data.notes) notesParts.push(`Customer message: ${data.notes}`);
      setForm((f) => ({
        ...f,
        customer_name: data.customer_name || f.customer_name,
        customer_email: data.customer_email || f.customer_email,
        customer_phone: data.customer_phone || f.customer_phone,
        start_datetime: startInput || f.start_datetime,
        end_datetime: endInput || f.end_datetime,
        pickup_location: data.pickup_location || f.pickup_location,
        internal_notes: notesParts.join("\n") || f.internal_notes,
      }));
      if (data.pickup_location || data.notes) setShowAdvanced(true);
    }).catch((e) => {
      // 404 → unknown inquiry, but don't block manual booking creation.
      toast.error(formatApiError(e));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromInquiryId]);

  // Phase 2F: Fleet Calendar's click-empty-slot handoff. Pure query-param
  // → form-state prefill, same pattern as the from_inquiry effect above but
  // no async fetch needed (the calendar passes literal values). All normal
  // validation (inline-warnings + the server-side POST /bookings check)
  // still runs before save: this only sets the form's initial state.
  useEffect(() => {
    if (!prefillVehicleId && !prefillStart && !prefillEnd) return;
    setForm((f) => ({
      ...f,
      item_id: prefillVehicleId || f.item_id,
      start_datetime: prefillStart ? toLocalInput(new Date(prefillStart)) : f.start_datetime,
      end_datetime: prefillEnd ? toLocalInput(new Date(prefillEnd)) : f.end_datetime,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillVehicleId, prefillStart, prefillEnd]);

  // Vehicle auto-pick from inquiry preferred_vehicle text, once vehicles load.
  useEffect(() => {
    if (!fromInquiry || !vehicles.length) return;
    const match = findVehicleMatch(vehicles, fromInquiry.preferred_vehicle);
    if (match) {
      setForm((f) => (f.item_id === match.id ? f : { ...f, item_id: match.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromInquiry, vehicles.length]);

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === form.item_id) || null,
    [vehicles, form.item_id]
  );
  const days = useMemo(
    () => computeDays(form.start_datetime, form.end_datetime),
    [form.start_datetime, form.end_datetime]
  );
  const autoPrice = useMemo(
    () => (selectedVehicle && days > 0 ? Number(selectedVehicle.price_per_day || 0) * days : 0),
    [selectedVehicle, days]
  );

  // Auto-apply the computed price whenever vehicle / dates change, unless user opted into manual.
  useEffect(() => {
    if (priceManual) return;
    if (autoPrice > 0) setForm((f) => ({ ...f, price: autoPrice }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrice, priceManual]);

  useEffect(() => {
    if (!form.item_id || !form.start_datetime || !form.end_datetime) return;
    const t = setTimeout(() => {
      const payload = {
        item_id: form.item_id,
        start_datetime: new Date(form.start_datetime).toISOString(),
        end_datetime: new Date(form.end_datetime).toISOString(),
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
      };
      api.post("/bookings/inline-warnings", payload)
        .then((r) => {
          setInlineWarnings(r.data);
          setConflicts(r.data.conflicts);
        })
        .catch(() => { setInlineWarnings(null); setConflicts(null); });
    }, 350);
    return () => clearTimeout(t);
  }, [form.item_id, form.start_datetime, form.end_datetime, form.customer_email, form.customer_phone]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    if (k === "customer_name" || k === "customer_email") setCustPicked(false);
    setForm({ ...form, [k]: v });
  };

  // Returning-customer search: debounced lookup as staff types the name or
  // email, so an existing customer can be selected instead of retyping (and
  // accidentally creating a duplicate record). Suppressed once a customer
  // has just been picked, until the field is edited again.
  const [custResults, setCustResults] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [custPicked, setCustPicked] = useState(false);
  useEffect(() => {
    if (custPicked) return;
    const query = form.customer_name || form.customer_email;
    if (!query || query.trim().length < 2) { setCustResults([]); setCustOpen(false); return; }
    const t = setTimeout(() => {
      api.get("/customers/search", { params: { q: query.trim(), limit: 6 } })
        .then((r) => { setCustResults(r.data || []); setCustOpen((r.data || []).length > 0); })
        .catch(() => { setCustResults([]); setCustOpen(false); });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer_name, form.customer_email, custPicked]);

  const pickCustomer = (c) => {
    setForm((f) => ({ ...f, customer_name: c.name || f.customer_name,
                       customer_email: c.email || f.customer_email,
                       customer_phone: c.phone || f.customer_phone }));
    setCustOpen(false);
    setCustPicked(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (inlineWarnings?.requires_override && !overrideNote.trim()) {
      toast.error("This booking has warnings: please provide an override note before saving.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        start_datetime: new Date(form.start_datetime).toISOString(),
        end_datetime: new Date(form.end_datetime).toISOString(),
        price: Number(form.price) || 0,
        deposit_required: Number(form.deposit_required) || 0,
        advance_payment_required: Number(form.advance_payment_required) || 0,
      };
      if (overrideNote.trim()) payload.override_note = overrideNote.trim();
      if (origin) payload.origin = origin;
      const endpoint = fromInquiryId
        ? `/inquiries/${fromInquiryId}/convert`
        : "/bookings";
      const { data } = await api.post(endpoint, payload);
      toast.success(
        fromInquiryId
          ? `Inquiry converted → Booking ${data.booking_reference} created`
          : `Booking ${data.booking_reference} created`
      );
      nav(`/app/bookings/${data.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.code === "inquiry_already_converted") {
        toast.error("This inquiry was already converted. Opening the existing booking…");
        if (detail.existing_booking_id) {
          nav(`/app/bookings/${detail.existing_booking_id}`, { replace: true });
        }
      } else if (status === 409) {
        toast.error("Warnings need an override note: type a reason below the warnings card.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onVehicleChange = (e) => {
    setForm({ ...form, item_id: e.target.value });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="booking-new-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">
          {fromInquiryId ? "Convert inquiry to booking" : "Add booking"}
        </h1>
        <p className="text-slate-600 mt-1">
          {fromInquiryId
            ? "Review the pre-filled details from the customer's inquiry, then create the booking. The customer will confirm via link."
            : "Create the booking first. The customer will confirm the details through a link."}
        </p>
      </div>

      {origin === "fleet_calendar" && (
        <div
          data-testid="from-fleet-calendar-banner"
          className="rounded-xl border border-[#1B4332]/20 bg-[#1B4332]/5 p-4 flex items-start gap-3"
        >
          <CalendarRange className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#1B4332]">
            Started from the Fleet Calendar: vehicle and dates are pre-filled below. Availability is still checked when you save.
          </div>
        </div>
      )}

      {fromInquiry && (
        <div
          data-testid="from-inquiry-banner"
          className="rounded-xl border border-[#1B4332]/20 bg-[#1B4332]/5 p-4 flex items-start gap-3"
        >
          <MessageSquare className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-[#1B4332]">
              Created from inquiry: {fromInquiry.customer_name}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {fromInquiry.customer_email}
              {fromInquiry.preferred_vehicle && ` · wants: ${fromInquiry.preferred_vehicle}`}
              {fromInquiry.start_date && ` · ${fromInquiry.start_date} → ${fromInquiry.end_date || "?"}`}
            </div>
            {fromInquiry.notes && (
              <div className="mt-2 text-xs text-slate-700 whitespace-pre-line border-l-2 border-[#1B4332]/30 pl-2 italic">
                "{fromInquiry.notes}"
              </div>
            )}
          </div>
        </div>
      )}

      {inlineWarnings && (inlineWarnings.conflicts?.has_critical
                           || inlineWarnings.conflicts?.has_warning
                           || (inlineWarnings.vehicle_warnings || []).length > 0) && (
        <InlineWarningsCard
          warnings={inlineWarnings}
          overrideNote={overrideNote}
          setOverrideNote={setOverrideNote}
          onChooseAnotherVehicle={() => { document.getElementById("bn-vehicle")?.focus(); }}
        />
      )}

      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 grid sm:grid-cols-2 gap-4" data-testid="booking-form">
        <div className="relative">
          <Field label="Customer name" required testId="bn-customer-name" value={form.customer_name}
                 onChange={set("customer_name")}
                 onFocus={() => { if (custResults.length > 0) setCustOpen(true); }}
                 onBlur={() => setTimeout(() => setCustOpen(false), 150)} />
          {custOpen && (
            <ul data-testid="bn-customer-suggestions"
                className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto">
              {custResults.map((c) => (
                <li key={c.email}>
                  <button type="button" data-testid={`bn-customer-suggestion-${c.email}`}
                          onMouseDown={() => pickCustomer(c)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                    <div className="font-medium text-slate-900">{c.name || c.email}</div>
                    <div className="text-xs text-slate-500">
                      Returning customer · {c.booking_count} previous booking{c.booking_count === 1 ? "" : "s"}
                      {c.last_rental_at ? ` · Last: ${new Date(c.last_rental_at).toLocaleDateString("en-PH", { month: "short", year: "numeric" })}` : ""}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Field label="Customer email" type="email" required testId="bn-customer-email" value={form.customer_email} onChange={set("customer_email")} />
        <Field label="Customer phone" testId="bn-customer-phone" value={form.customer_phone} onChange={set("customer_phone")} />
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Vehicle</span>
          <select id="bn-vehicle" data-testid="bn-vehicle" value={form.item_id} onChange={onVehicleChange} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.name} {v.plate_number ? `(${v.plate_number})` : ""}</option>
            ))}
          </select>
        </label>
        <Field label="Start" type="datetime-local" required testId="bn-start" value={form.start_datetime} onChange={set("start_datetime")} />
        <Field label="End" type="datetime-local" required testId="bn-end" value={form.end_datetime} onChange={set("end_datetime")} />

        <label className="sm:col-span-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Price (PHP)</span>
            {priceManual ? (
              <button
                type="button"
                onClick={() => {
                  setPriceManual(false);
                  if (autoPrice > 0) setForm((f) => ({ ...f, price: autoPrice }));
                }}
                data-testid="bn-price-auto-btn"
                className="text-[11px] text-[#1B4332] hover:underline inline-flex items-center gap-1"
              >
                <Calculator className="w-3 h-3" /> Use automatic
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setPriceManual(true)}
                data-testid="bn-price-manual-btn"
                className="text-[11px] text-[#1B4332] hover:underline inline-flex items-center gap-1"
              >
                <Pencil className="w-3 h-3" /> Set manually
              </button>
            )}
          </div>
          <input
            data-testid="bn-price"
            type="number"
            value={form.price}
            onChange={(e) => { setPriceManual(true); setForm({ ...form, price: e.target.value }); }}
            readOnly={!priceManual}
            className={`mt-1.5 w-full px-3.5 py-2.5 rounded-lg border outline-none ${
              priceManual
                ? "border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] bg-white"
                : "border-slate-200 bg-slate-50 text-slate-700 cursor-default"
            }`}
          />
          {selectedVehicle && days > 0 ? (
            <p className="text-[11px] text-slate-500 mt-1" data-testid="bn-price-breakdown">
              {priceManual
                ? `Manual override · auto would be ${fmtCurrency(selectedVehicle.price_per_day)} × ${days} day${days === 1 ? "" : "s"} = ${fmtCurrency(autoPrice)}`
                : `Auto · ${fmtCurrency(selectedVehicle.price_per_day)}/day × ${days} day${days === 1 ? "" : "s"} = ${fmtCurrency(autoPrice)}`}
            </p>
          ) : (
            <p className="text-[11px] text-slate-500 mt-1">
              Pick a vehicle and valid start/end dates to auto-calculate the price.
            </p>
          )}
        </label>

        <label className="sm:col-span-2 flex items-center gap-2 mt-1">
          <input type="checkbox" checked={form.send_confirmation_now} onChange={set("send_confirmation_now")} data-testid="bn-send-now" />
          <span className="text-sm">Send confirmation link to customer now</span>
        </label>

        {/* Advanced: tucked away. Most bookings don't need any of this. */}
        <div className="sm:col-span-2 pt-2 border-t border-slate-200">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="bn-toggle-advanced"
            className="inline-flex items-center gap-1 text-sm text-[#1B4332] font-medium hover:underline"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showAdvanced ? "Hide" : "Show"} more details (pickup location, notes, deposit)
          </button>
        </div>

        {showAdvanced && (
          <>
            {/* Location Pickers */}
            <div className="sm:col-span-2 space-y-4">
              <BookingLocationPicker
                label="Pickup Location"
                value={form.pickup_location_data}
                onChange={(data) => setForm({ ...form, pickup_location_data: data, pickup_location: data?.label || data?.address || '' })}
                businessLocation={businessLocation}
                showMap={showPickupMap}
                onToggleMap={() => setShowPickupMap(!showPickupMap)}
              />
            </div>
            
            <div className="sm:col-span-2 space-y-4">
              <BookingLocationPicker
                label="Return Location"
                value={form.return_location_data}
                onChange={(data) => setForm({ ...form, return_location_data: data, return_location: data?.label || data?.address || '' })}
                businessLocation={businessLocation}
                showMap={showReturnMap}
                onToggleMap={() => setShowReturnMap(!showReturnMap)}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.return_same_as_pickup}
                  onChange={(e) => setForm({ ...form, return_same_as_pickup: e.target.checked })}
                />
                Return to same location as pickup
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.delivery_required}
                  onChange={(e) => setForm({ ...form, delivery_required: e.target.checked })}
                />
                Delivery/pickup service required
              </label>
              {company?.driver_services_enabled && (
                <>
                  <label className="flex items-center gap-2 text-sm" data-testid="bn-with-driver-label">
                    <input
                      type="checkbox"
                      data-testid="bn-with-driver"
                      checked={form.rental_service_type === "with_driver"}
                      onChange={(e) => setForm({ ...form, rental_service_type: e.target.checked ? "with_driver" : "self_drive" })}
                    />
                    Rental includes a company driver
                  </label>
                  {form.rental_service_type === "with_driver" && (
                    <p className="text-xs text-slate-500 pl-6">
                      Assign the driver and set the driver-service price after creating the booking, from the booking's detail page.
                    </p>
                  )}
                </>
              )}
            </div>
            
            <Field label="Security deposit required (PHP)" type="number" testId="bn-deposit" value={form.deposit_required} onChange={set("deposit_required")} />
            <Field label="Advance payment required (PHP)" type="number" testId="bn-advance-payment" value={form.advance_payment_required} onChange={set("advance_payment_required")} />
            <p className="sm:col-span-2 -mt-2 text-xs text-slate-500">
              The security deposit is held and returned. An advance payment is part of the rental price paid early
              and is applied to the rental charge.
            </p>
            <label className="sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Customer-visible notes (rules, what to bring)</span>
              <textarea data-testid="bn-customer-notes" value={form.customer_visible_notes} onChange={set("customer_visible_notes")} rows={2} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </label>
            <label className="sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Internal notes (only your team sees this)</span>
              <textarea data-testid="bn-internal-notes" value={form.internal_notes} onChange={set("internal_notes")} rows={2} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
            </label>
          </>
        )}

        <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-2">
          <div className="mr-auto text-sm text-slate-600">
            Will be created as <span className="font-medium text-orange-700">Pending Client Confirmation</span> · Price preview: <span className="font-mono">{fmtCurrency(Number(form.price))}</span>
          </div>
          <button type="button" onClick={() => nav(-1)} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm" data-testid="bn-cancel">Cancel</button>
          <button
            type="submit"
            disabled={submitting || (inlineWarnings?.requires_override && !overrideNote.trim())}
            className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60"
            data-testid="bn-submit"
          >
            {submitting ? "Creating…" : (fromInquiryId ? "Convert to booking" : "Create booking")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, onFocus, onBlur, type = "text", required, testId, className = "" }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none"
      />
    </label>
  );
}

function InlineWarningsCard({ warnings, overrideNote, setOverrideNote, onChooseAnotherVehicle }) {
  const sev = warnings.severity;
  const style = severityStyle(sev === "none" ? "info" : sev);
  const overlaps = warnings.conflicts?.overlaps || [];
  const buffers = warnings.conflicts?.buffer_warnings || [];
  const vw = warnings.vehicle_warnings || [];
  return (
    <div
      className={`rounded-xl border ${style.banner} overflow-hidden`}
      data-testid="inline-warnings-card"
    >
      <div className={`h-1 ${style.bar}`} />
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-lg grid place-items-center bg-white border ${style.chip}`}>
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${style.title}`}>{style.label}</div>
            <div className={`font-semibold ${style.title}`} data-testid="inline-warnings-summary">{warnings.summary}</div>
            <div className="mt-2 space-y-1 text-sm">
              {overlaps.map((o) => (
                <div key={o.id} className="text-red-900 flex items-start gap-2" data-testid={`inline-overlap-${o.id}`}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                  <span>
                    Conflicts with <b>#{o.booking_reference}</b> ({o.customer_name}): {o.status}.
                  </span>
                </div>
              ))}
              {buffers.map((o) => (
                <div key={o.id} className="text-amber-900 flex items-start gap-2" data-testid={`inline-buffer-${o.id}`}>
                  <CalendarClock className="w-3.5 h-3.5 mt-0.5" />
                  <span>
                    Only {o.gap_hours}h between #{o.booking_reference} and this booking (buffer {o.buffer_required}h).
                  </span>
                </div>
              ))}
              {vw.map((w, i) => {
                const Icon = w.code === "vehicle_maintenance" ? Wrench : AlertOctagon;
                return (
                  <div key={i} className="text-slate-800 flex items-start gap-2" data-testid={`inline-vehicle-${w.code}`}>
                    <Icon className="w-4 h-4 mt-0.5" />
                    <span>{w.message}</span>
                  </div>
                );
              })}
              {(warnings.location_warnings || []).map((w, i) => (
                <div key={`loc-${i}`} className="text-blue-900 flex items-start gap-2" data-testid={`inline-location-${i}`}>
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span><b>[{w.severity}]</b> {w.message}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onChooseAnotherVehicle}
                data-testid="inline-warning-choose-vehicle"
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
              >
                Choose another vehicle
              </button>
            </div>
            {warnings.requires_override && (
              <div className="mt-3">
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-red-700">
                    Override note (required to save)
                  </span>
                  <textarea
                    data-testid="inline-warning-override"
                    rows={2}
                    value={overrideNote}
                    onChange={(e) => setOverrideNote(e.target.value)}
                    placeholder="e.g. Customer agreed to shared pickup; owner approved."
                    className="mt-1 w-full text-sm rounded-md border border-red-300 p-2"
                  />
                </label>
                <div className="text-[11px] text-slate-600 mt-1">
                  This note is saved in the booking's audit history.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
